import React from 'react';
import {
  Alert,
  Box,
  Button,
  ButtonBase,
  Checkbox,
  CircularProgress,
  MenuItem,
  Paper,
  Slider,
  Stack,
  TextField,
  ToggleButton,
  ToggleButtonGroup,
  Typography,
} from '@mui/material';
import CenterFocusStrongOutlinedIcon from '@mui/icons-material/CenterFocusStrongOutlined';
import CropFreeOutlinedIcon from '@mui/icons-material/CropFreeOutlined';
import ImageOutlinedIcon from '@mui/icons-material/ImageOutlined';
import LayersOutlinedIcon from '@mui/icons-material/LayersOutlined';
import RestartAltIcon from '@mui/icons-material/RestartAlt';
import TuneOutlinedIcon from '@mui/icons-material/TuneOutlined';
import { labelize } from '../../components/workspace/WorkspaceStatus';
import { normalizePlacementTransform } from './launchWorkflow';
import {
  loadPlacementPreviewTemplate,
  placementCanvasForVariant,
  placementSupportsVariant,
  productPreviewProfile,
} from './productPreview';

const percent = (value) => Math.round(Number(value || 0) * 100);
const round = (value, precision = 4) => Number(Number(value).toFixed(precision));
const clamp = (value, minimum, maximum) => Math.min(maximum, Math.max(minimum, value));

const placementLabel = (placement) => placement?.display_name || labelize(placement?.placement_key) || 'Print area';

function RangeControl({ label, value, minimum, maximum, step, valueLabel, onChange }) {
  return (
    <Box>
      <Stack direction="row" justifyContent="space-between" spacing={1}>
        <Typography component="label" variant="caption" color="text.secondary">{label}</Typography>
        <Typography variant="caption" color="text.primary">{valueLabel(value)}</Typography>
      </Stack>
      <Slider
        size="small"
        aria-label={label}
        value={value}
        min={minimum}
        max={maximum}
        step={step}
        onChange={(event, next) => onChange(Array.isArray(next) ? next[0] : next)}
      />
    </Box>
  );
}

/**
 * A provider-neutral print-area editor. The authoritative canvas represents
 * the printable surface, while optional provider SVG templates supply exact
 * panel context without pretending catalog photography has print coordinates.
 * Coordinates are normalized so the base placement can be translated onto
 * variant canvases with different pixel dimensions.
 */
export default function ProductPlacementDesigner({
  placements = [],
  selectedKeys = [],
  transforms = {},
  variants = [],
  selectedVariantIDs = [],
  artwork,
  productImageURL,
  onToggle,
  onTransformChange,
}) {
  const selectedSet = React.useMemo(() => new Set(selectedKeys), [selectedKeys]);
  const selectedPlacements = React.useMemo(
    () => placements.filter((item) => selectedSet.has(item.placement_key)),
    [placements, selectedSet],
  );
  const selectedVariantSet = React.useMemo(() => new Set(selectedVariantIDs.map(String)), [selectedVariantIDs]);
  const selectedVariants = React.useMemo(
    () => variants.filter((item) => selectedVariantSet.has(String(item.id))),
    [variants, selectedVariantSet],
  );
  const [activeKey, setActiveKey] = React.useState(selectedPlacements[0]?.placement_key || '');
  const activePlacement = selectedPlacements.find((item) => item.placement_key === activeKey) || selectedPlacements[0] || null;
  const previewVariants = React.useMemo(
    () => selectedVariants.filter((item) => placementSupportsVariant(activePlacement, item.external_id)),
    [activePlacement, selectedVariants],
  );
  const [activeVariantID, setActiveVariantID] = React.useState(previewVariants[0]?.id || '');
  const [previewMode, setPreviewMode] = React.useState('print-area');
  const [referenceImageFailed, setReferenceImageFailed] = React.useState(false);
  const [templateImageFailed, setTemplateImageFailed] = React.useState(false);
  const [resolvedPreviewTemplate, setResolvedPreviewTemplate] = React.useState(null);
  const [templateStatus, setTemplateStatus] = React.useState('idle');
  const [templateError, setTemplateError] = React.useState('');
  const previewModeTouched = React.useRef(false);
  const canvasRef = React.useRef(null);
  const dragRef = React.useRef(null);

  React.useEffect(() => {
    if (!selectedPlacements.some((item) => item.placement_key === activeKey)) {
      setActiveKey(selectedPlacements[0]?.placement_key || '');
    }
  }, [activeKey, selectedPlacements]);

  React.useEffect(() => {
    setReferenceImageFailed(false);
  }, [productImageURL]);

  React.useEffect(() => {
    if (!previewVariants.some((item) => String(item.id) === String(activeVariantID))) {
      setActiveVariantID(previewVariants[0]?.id || '');
    }
  }, [activeVariantID, previewVariants]);

  const activeVariant = previewVariants.find((item) => String(item.id) === String(activeVariantID)) || previewVariants[0] || null;
  const activeVariantExternalID = activeVariant?.external_id;
  const transform = normalizePlacementTransform(activePlacement ? transforms[activePlacement.placement_key] || activePlacement.default_transform : null);
  const variantCanvas = placementCanvasForVariant(activePlacement, activeVariantExternalID);
  const canvasWidth = Number(variantCanvas.width || 0);
  const canvasHeight = Number(variantCanvas.height || 0);
  const artworkWidth = Number(artwork?.width || 0);
  const artworkHeight = Number(artwork?.height || 0);
  const resolvedCanvasAspect = Number(resolvedPreviewTemplate?.canvas_aspect || 0);
  const canvasAspect = resolvedCanvasAspect > 0
    ? resolvedCanvasAspect
    : (canvasWidth > 0 && canvasHeight > 0 ? canvasWidth / canvasHeight : 1);
  const canvasGeometryKnown = resolvedCanvasAspect > 0 || (canvasWidth > 0 && canvasHeight > 0);
  const artworkAspect = artworkWidth > 0 && artworkHeight > 0 ? artworkWidth / artworkHeight : 1;
  const artworkWidthPercent = 100 * transform.scale;
  const artworkHeightPercent = artworkAspect > 0 ? artworkWidthPercent * (canvasAspect / artworkAspect) : artworkWidthPercent;
  const previewProfile = productPreviewProfile({
    placement: activePlacement,
    imageURL: productImageURL,
    canvasAspect,
    resolvedProfile: resolvedPreviewTemplate,
  });
  const templatePreviewAvailable = Boolean(previewProfile.calibrated && previewProfile.imageURL && !templateImageFailed);
  const referencePreviewAvailable = Boolean(productImageURL && !referenceImageFailed);
  const visiblePreviewMode = previewMode === 'template' && templatePreviewAvailable
    ? 'template'
    : (previewMode === 'reference' && referencePreviewAvailable ? 'reference' : 'print-area');

  React.useEffect(() => {
    const controller = new AbortController();
    setResolvedPreviewTemplate(null);
    setTemplateImageFailed(false);
    setTemplateError('');
    if (!activePlacement) {
      setTemplateStatus('idle');
      return () => controller.abort();
    }
    setTemplateStatus('loading');
    loadPlacementPreviewTemplate({
      placement: activePlacement,
      externalVariantID: activeVariantExternalID,
      signal: controller.signal,
    })
      .then((profile) => {
        if (controller.signal.aborted) return;
        setResolvedPreviewTemplate(profile);
        setTemplateStatus(profile ? 'ready' : 'unavailable');
        if (profile && !previewModeTouched.current) setPreviewMode('template');
      })
      .catch((error) => {
        if (controller.signal.aborted) return;
        setTemplateStatus('error');
        setTemplateError(error.message || 'The provider print template could not be loaded.');
      });
    return () => controller.abort();
  }, [activePlacement, activeVariantExternalID]);

  const updateTransform = React.useCallback((patch) => {
    if (!activePlacement) return;
    onTransformChange(activePlacement.placement_key, normalizePlacementTransform({ ...transform, ...patch }));
  }, [activePlacement, onTransformChange, transform]);

  const beginDrag = (event) => {
    if (!canvasRef.current || !activePlacement) return;
    event.preventDefault();
    event.currentTarget.setPointerCapture?.(event.pointerId);
    dragRef.current = {
      pointerID: event.pointerId,
      clientX: event.clientX,
      clientY: event.clientY,
      x: transform.x,
      y: transform.y,
    };
  };

  const continueDrag = (event) => {
    const drag = dragRef.current;
    const canvas = canvasRef.current;
    if (!drag || !canvas || drag.pointerID !== event.pointerId) return;
    const bounds = canvas.getBoundingClientRect();
    if (!bounds.width || !bounds.height) return;
    updateTransform({
      x: round(clamp(drag.x + ((event.clientX - drag.clientX) / bounds.width), -1, 2)),
      y: round(clamp(drag.y + ((event.clientY - drag.clientY) / bounds.height), -1, 2)),
    });
  };

  const finishDrag = (event) => {
    if (dragRef.current?.pointerID !== event.pointerId) return;
    event.currentTarget.releasePointerCapture?.(event.pointerId);
    dragRef.current = null;
  };

  const nudgeArtwork = (event) => {
    const amount = event.shiftKey ? 0.05 : 0.01;
    const offsets = {
      ArrowLeft: [-amount, 0],
      ArrowRight: [amount, 0],
      ArrowUp: [0, -amount],
      ArrowDown: [0, amount],
    };
    if (!offsets[event.key]) return;
    event.preventDefault();
    updateTransform({
      x: round(clamp(transform.x + offsets[event.key][0], -1, 2)),
      y: round(clamp(transform.y + offsets[event.key][1], -1, 2)),
    });
  };

  const artworkLayer = ({ opacity = 1, blendMode = 'normal' } = {}) => (
    <Box
      component="button"
      type="button"
      aria-label={`Move ${artwork?.name || 'artwork'} on ${placementLabel(activePlacement)}. Use arrow keys to nudge; hold Shift for larger steps.`}
      onPointerDown={beginDrag}
      onKeyDown={nudgeArtwork}
      sx={{
        position: 'absolute',
        left: `${transform.x * 100}%`,
        top: `${transform.y * 100}%`,
        width: `${artworkWidthPercent}%`,
        height: `${artworkHeightPercent}%`,
        maxWidth: 'none',
        maxHeight: 'none',
        p: 0,
        m: 0,
        border: 0,
        bgcolor: 'transparent',
        cursor: dragRef.current ? 'grabbing' : 'grab',
        transform: `translate(-50%, -50%) rotate(${transform.angle}deg)`,
        transformOrigin: 'center',
        lineHeight: 0,
        opacity,
        mixBlendMode: blendMode,
        '&:focus-visible': { outline: '3px solid', outlineColor: 'secondary.main', outlineOffset: 3 },
      }}
    >
      <Box
        component="img"
        src={artwork?.src}
        alt=""
        draggable={false}
        sx={{ display: 'block', width: '100%', height: '100%', objectFit: 'contain', pointerEvents: 'none', filter: 'drop-shadow(0 2px 4px rgba(0,0,0,.18))' }}
      />
    </Box>
  );

  const printAreaCanvas = (
    <Box
      ref={canvasRef}
      aria-label={`${placementLabel(activePlacement)} printable surface`}
      onPointerMove={continueDrag}
      onPointerUp={finishDrag}
      onPointerCancel={finishDrag}
      sx={{
        position: 'relative',
        width: '100%',
        maxWidth: 520,
        maxHeight: 520,
        aspectRatio: `${canvasAspect}`,
        overflow: 'hidden',
        border: '2px solid',
        borderColor: 'primary.main',
        borderRadius: 1.5,
        backgroundColor: 'grey.100',
        backgroundImage: 'linear-gradient(45deg, rgba(0,0,0,.045) 25%, transparent 25%), linear-gradient(-45deg, rgba(0,0,0,.045) 25%, transparent 25%), linear-gradient(45deg, transparent 75%, rgba(0,0,0,.045) 75%), linear-gradient(-45deg, transparent 75%, rgba(0,0,0,.045) 75%)',
        backgroundSize: '24px 24px',
        backgroundPosition: '0 0, 0 12px, 12px -12px, -12px 0',
        touchAction: 'none',
      }}
    >
      {artworkLayer()}
      <Typography
        variant="caption"
        aria-hidden="true"
        sx={{ position: 'absolute', left: 8, bottom: 6, px: 0.75, py: 0.25, borderRadius: 0.5, bgcolor: 'rgba(255,255,255,.82)', color: 'text.secondary' }}
      >
        {canvasGeometryKnown ? 'Provider print area' : 'Normalized print area'}
      </Typography>
    </Box>
  );

  const templateCanvas = templatePreviewAvailable ? (
    <Box
      aria-label={`${placementLabel(activePlacement)} provider print-template preview`}
      sx={{
        position: 'relative',
        width: '100%',
        maxWidth: 520,
        aspectRatio: `${previewProfile.imageAspect}`,
        overflow: 'hidden',
        border: 1,
        borderColor: 'divider',
        borderRadius: 1.5,
        bgcolor: 'grey.100',
      }}
    >
      <Box
        component="img"
        src={previewProfile.imageURL}
        alt={`${placementLabel(activePlacement)} provider print template`}
        draggable={false}
        onError={() => setTemplateImageFailed(true)}
        sx={{ position: 'absolute', inset: 0, display: 'block', width: '100%', height: '100%', objectFit: 'contain', pointerEvents: 'none' }}
      />
      <Box
        ref={canvasRef}
        onPointerMove={continueDrag}
        onPointerUp={finishDrag}
        onPointerCancel={finishDrag}
        sx={{
          position: 'absolute',
          left: `${previewProfile.frame.x * 100}%`,
          top: `${previewProfile.frame.y * 100}%`,
          width: `${previewProfile.frame.width * 100}%`,
          height: `${previewProfile.frame.height * 100}%`,
          transform: `translate(-50%, -50%) rotate(${previewProfile.frame.angle}deg)`,
          transformOrigin: 'center',
          overflow: 'hidden',
          border: '1px dashed',
          borderColor: 'primary.main',
          borderRadius: `${previewProfile.frame.borderRadius * 100}%`,
          backgroundColor: 'rgba(255,255,255,.08)',
          touchAction: 'none',
        }}
      >
        {artworkLayer({ opacity: previewProfile.frame.opacity, blendMode: previewProfile.frame.blendMode })}
      </Box>
      <Typography
        variant="caption"
        aria-hidden="true"
        sx={{ position: 'absolute', left: 8, bottom: 6, px: 0.75, py: 0.25, borderRadius: 0.5, bgcolor: 'rgba(255,255,255,.86)', color: 'text.secondary' }}
      >
        Exact provider print template
      </Typography>
    </Box>
  ) : null;

  const referenceCanvas = referencePreviewAvailable ? (
    <Box
      aria-label="Provider product reference"
      sx={{
        position: 'relative',
        width: '100%',
        maxWidth: 520,
        aspectRatio: '1 / 1',
        overflow: 'hidden',
        border: 1,
        borderColor: 'divider',
        borderRadius: 1.5,
        bgcolor: 'grey.100',
      }}
    >
      <Box
        component="img"
        src={productImageURL}
        alt="Blank product reference supplied by the fulfillment provider"
        draggable={false}
        onError={() => setReferenceImageFailed(true)}
        sx={{ position: 'absolute', inset: 0, display: 'block', width: '100%', height: '100%', objectFit: 'contain' }}
      />
      <Typography
        variant="caption"
        sx={{ position: 'absolute', left: 8, bottom: 6, px: 0.75, py: 0.25, borderRadius: 0.5, bgcolor: 'rgba(255,255,255,.86)', color: 'text.secondary' }}
      >
        Product reference · artwork is not rendered here
      </Typography>
    </Box>
  ) : null;

  return (
    <Stack spacing={2}>
      <Stack direction={{ xs: 'column', md: 'row' }} spacing={2} alignItems={{ md: 'stretch' }}>
        <Paper variant="outlined" sx={{ width: { md: 260 }, flexShrink: 0, p: 1.5, bgcolor: 'background.default' }}>
          <Typography variant="subtitle2" gutterBottom>Print areas</Typography>
          <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1 }}>
            Choose where this artwork should be printed.
          </Typography>
          <Stack spacing={0.25}>
            {placements.map((placement) => {
              const selected = selectedSet.has(placement.placement_key);
              return (
                <Box
                  key={placement.id || placement.placement_key}
                  sx={{
                    borderRadius: 1,
                    border: 1,
                    borderColor: selected && activePlacement?.placement_key === placement.placement_key ? 'primary.main' : 'transparent',
                    bgcolor: selected && activePlacement?.placement_key === placement.placement_key ? 'action.selected' : 'transparent',
                  }}
                >
                  <Stack direction="row" alignItems="center" sx={{ minHeight: 44, pr: 0.5 }}>
                    <Checkbox
                      checked={selected}
                      onChange={() => onToggle(placement.placement_key)}
                      inputProps={{ 'aria-label': `${selected ? 'Remove' : 'Add'} ${placementLabel(placement)} print area` }}
                    />
                    <ButtonBase
                      onClick={() => {
                        if (!selected) onToggle(placement.placement_key);
                        setActiveKey(placement.placement_key);
                      }}
                      sx={{ minWidth: 0, flex: 1, alignSelf: 'stretch', justifyContent: 'flex-start', textAlign: 'left', borderRadius: 0.75 }}
                    >
                      <Box component="span" sx={{ display: 'block', minWidth: 0 }}>
                        <Typography component="span" variant="body2" sx={{ display: 'block' }}>{placementLabel(placement)}</Typography>
                        <Typography component="span" variant="caption" color="text.secondary" sx={{ display: 'block' }}>
                          {placement.width && placement.height ? `${placement.width} × ${placement.height}` : 'Responsive provider canvas'}
                        </Typography>
                      </Box>
                    </ButtonBase>
                  </Stack>
                </Box>
              );
            })}
          </Stack>
        </Paper>

        <Paper variant="outlined" sx={{ minWidth: 0, flex: 1, p: { xs: 1.5, sm: 2 } }}>
          {activePlacement ? (
            <Stack spacing={2}>
              <Stack direction={{ xs: 'column', sm: 'row' }} justifyContent="space-between" spacing={1}>
                <Box>
                  <Typography variant="subtitle2">{placementLabel(activePlacement)}</Typography>
                  <Typography variant="caption" color="text.secondary">
                    Layer 1 · {previewVariants.length === selectedVariants.length
                      ? 'all selected variants'
                      : `${previewVariants.length} of ${selectedVariants.length} selected variants`}
                    {activeVariant ? ` · previewing ${activeVariant.title || activeVariant.external_id}` : ''}
                  </Typography>
                </Box>
                <Stack direction="row" spacing={1}>
                  <Button size="small" startIcon={<CenterFocusStrongOutlinedIcon />} onClick={() => updateTransform({ x: 0.5, y: 0.5 })}>
                    Center
                  </Button>
                  <Button size="small" startIcon={<RestartAltIcon />} onClick={() => onTransformChange(activePlacement.placement_key, normalizePlacementTransform(activePlacement.default_transform))}>
                    Reset
                  </Button>
                </Stack>
              </Stack>

              {previewVariants.length > 1 ? (
                <TextField
                  select
                  size="small"
                  label="Preview variant"
                  value={activeVariant?.id || ''}
                  onChange={(event) => setActiveVariantID(event.target.value)}
                  sx={{ width: { xs: '100%', sm: 360 } }}
                  helperText="The saved base placement is shared; the canvas and provider template follow this variant."
                >
                  {previewVariants.map((variant) => (
                    <MenuItem key={variant.id} value={variant.id}>
                      {variant.title || variant.external_id}
                    </MenuItem>
                  ))}
                </TextField>
              ) : null}

              <ToggleButtonGroup
                exclusive
                size="small"
                value={visiblePreviewMode}
                onChange={(event, nextMode) => {
                  if (!nextMode) return;
                  previewModeTouched.current = true;
                  setPreviewMode(nextMode);
                }}
                aria-label="Artwork preview mode"
                sx={{ alignSelf: 'flex-start' }}
              >
                <ToggleButton value="template" disabled={!templatePreviewAvailable} aria-label="Show provider print template">
                  {templateStatus === 'loading' ? <CircularProgress size={16} sx={{ mr: 0.75 }} /> : <LayersOutlinedIcon fontSize="small" sx={{ mr: 0.75 }} />}
                  Print template
                </ToggleButton>
                <ToggleButton value="print-area" aria-label="Show exact print area">
                  <CropFreeOutlinedIcon fontSize="small" sx={{ mr: 0.75 }} />
                  Print area
                </ToggleButton>
                <ToggleButton value="reference" disabled={!referencePreviewAvailable} aria-label="Show blank product reference">
                  <ImageOutlinedIcon fontSize="small" sx={{ mr: 0.75 }} />
                  Product blank
                </ToggleButton>
              </ToggleButtonGroup>

              <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', lg: 'minmax(280px, 1.4fr) minmax(240px, 0.8fr)' }, gap: 2, alignItems: 'center' }}>
                <Box sx={{ display: 'grid', placeItems: 'center', minHeight: 320, overflow: 'hidden', p: 1 }}>
                  {visiblePreviewMode === 'template' ? templateCanvas : (visiblePreviewMode === 'reference' ? referenceCanvas : printAreaCanvas)}
                </Box>

                <Stack spacing={1.5}>
                  <Stack direction="row" spacing={1} alignItems="center">
                    <TuneOutlinedIcon color="action" fontSize="small" />
                    <Typography variant="subtitle2">Layer position</Typography>
                  </Stack>
                  <RangeControl label="Horizontal" value={transform.x} minimum={-0.25} maximum={1.25} step={0.01} valueLabel={(value) => `${percent(value)}%`} onChange={(x) => updateTransform({ x })} />
                  <RangeControl label="Vertical" value={transform.y} minimum={-0.25} maximum={1.25} step={0.01} valueLabel={(value) => `${percent(value)}%`} onChange={(y) => updateTransform({ y })} />
                  <RangeControl label="Scale" value={transform.scale} minimum={0.1} maximum={4} step={0.01} valueLabel={(value) => `${percent(value)}%`} onChange={(scale) => updateTransform({ scale })} />
                  <RangeControl label="Rotation" value={transform.angle} minimum={-180} maximum={180} step={1} valueLabel={(value) => `${Math.round(value)}°`} onChange={(angle) => updateTransform({ angle })} />
                  <Typography variant="caption" color="text.secondary">
                    Drag the artwork or use the controls. Arrow keys nudge by 1%; Shift + arrow nudges by 5%.
                  </Typography>
                  {visiblePreviewMode === 'template' ? (
                    <Alert severity="success" variant="outlined">
                      This canvas is aligned to the provider’s SVG print template for the selected placement and preview variant. A generated mockup may still include folds, seams, and perspective that this layout view does not simulate.
                    </Alert>
                  ) : null}
                  {visiblePreviewMode === 'reference' ? (
                    <Alert severity="info" variant="outlined">
                      The catalog photograph is a product reference only. Printify does not publish coordinates that map its print templates onto this photograph, so PODBoss no longer places a misleading rectangle over it.
                    </Alert>
                  ) : null}
                  {templateStatus === 'error' ? <Alert severity="warning" variant="outlined">{templateError}</Alert> : null}
                  {templateStatus === 'unavailable' ? (
                    <Alert severity="info" variant="outlined">
                      This offer has no cached provider print template yet. The normalized print-area editor remains usable; syncing its Printify shop will import template metadata when Printify supplies it.
                    </Alert>
                  ) : null}
                </Stack>
              </Box>
            </Stack>
          ) : (
            <Stack alignItems="center" justifyContent="center" spacing={0.75} sx={{ minHeight: 360, textAlign: 'center', p: 3 }}>
              <TuneOutlinedIcon color="disabled" sx={{ fontSize: 44 }} />
              <Typography variant="subtitle2">Choose a print area to begin</Typography>
              <Typography variant="body2" color="text.secondary">Your artwork placement is saved independently from any fulfillment provider.</Typography>
            </Stack>
          )}
        </Paper>
      </Stack>

      <Alert severity="info">
        This first version applies one artwork layer to all selected variants using normalized coordinates. Printify will map it to each variant’s own canvas. The placement model already supports multiple layers and variant-specific overrides; those editing controls can be added without changing saved Products.
      </Alert>
    </Stack>
  );
}
